import { Controller } from "@hotwired/stimulus";

/**
 * Painting Demo Canvas Controller (Restaurant Audit,
 * Step 6).
 *
 * Drives the toggle between the skeleton ("Tu sitio
 * se está pintando…") and the painted preview ("Tu
 * nuevo sitio") for the painting_demo step.
 *
 * Architecture (CAL-264). The painting_demo canvas
 * is rendered ONCE on page load and never swapped by
 * `audit-stage`. The `done` Stimulus value drives
 * which slot is visible. The painted slot's HTML
 * (the editorial copy + the iPhone-frame venue
 * preview) is filled either by the server at first
 * paint (when the audit is already painted) or by a
 * Turbo Stream `update` broadcast targeting the
 * painted slot's stable id (when painting completes
 * mid-session). Either way, by the time `done` flips
 * to `"true"`, the slot is already populated: this
 * controller just toggles visibility.
 *
 * Lifecycle:
 *
 * 1. **connect()**: show the skeleton or painted
 *    slot based on the `done` value at attach time.
 *    If `done="true"`, dispatch `audit-step:complete`
 *    so the (hidden) Continue button can flip
 *    enabled.
 *
 * 2. **`doneValueChanged`**: fires when the
 *    broadcast lands `done="true"` after painting
 *    completes server-side. Hides the skeleton,
 *    shows the painted slot, dispatches completion.
 *
 * The Continue button is HIDDEN for painting_demo
 * (terminal step), but we still dispatch
 * `audit-step:complete` for parity with the other
 * steps' completion contract.
 */
export default class extends Controller {
  static values = {
    done: { type: String, default: "false" },
  };

  static targets = ["skeleton", "painted"];

  connect() {
    this.completed = false;
    this.applyState();
  }

  doneValueChanged(value, previousValue) {
    // Stimulus fires every value-changed callback
    // once on connect with previousValue=undefined.
    // The connect() flow handles initial state.
    if (previousValue === undefined) return;
    if (value === previousValue) return;
    this.applyState();
  }

  applyState() {
    if (this.doneValue === "true") {
      this.showPainted();
    } else {
      this.showSkeleton();
    }
  }

  showSkeleton() {
    if (this.hasSkeletonTarget) {
      this.skeletonTarget.style.display = "";
    }
    if (this.hasPaintedTarget) {
      this.paintedTarget.style.display = "none";
    }
  }

  showPainted() {
    if (this.hasSkeletonTarget) {
      this.skeletonTarget.style.display = "none";
    }
    if (this.hasPaintedTarget) {
      this.paintedTarget.style.display = "";
    }
    this.signalComplete();
  }

  signalComplete() {
    if (this.completed) return;
    this.completed = true;

    // Stamp the parent stage element with a
    // completion flag BEFORE dispatching the event.
    // Stimulus controller connect order is non-
    // deterministic: if this controller connects
    // and fires the event before audit-continue-
    // button attaches its listener, the event is
    // lost. The flag lets the button's connect()
    // see the completion regardless of attach order.
    const stage = this.element.closest(
      '[data-audit-stage-target="stage"]'
    );
    if (stage) {
      stage.dataset.auditStepComplete = "true";
    }

    document.dispatchEvent(
      new CustomEvent("audit-step:complete", {
        detail: { stepKey: "painting_demo" },
      })
    );
  }
}
